// w19 / w11 Celestial Foil — paper-invitation preview, celestial mood.
// Outer night-sky foil, inner card uses template's bg/ink/accent.
// Eyebrow "TO'Y MAROSIMI" → starry sun-moon medallion (or photo) →
// names in template font → date + venue caption.

import { Photo, type LayoutCtx } from "./_shared";

const NIGHT_GRADIENT =
  "linear-gradient(135deg, #08111E 0%, #1B2236 22%, #2E2A40 45%, #1B2236 65%, #050810 100%)";

export function CelestialFoilLayout({
  names, date, venue, accent, ink, bg, font, photo,
}: LayoutCtx) {
  const captionDate = formatDate(date);
  return (
    <div style={{ position: "absolute", inset: 0, background: NIGHT_GRADIENT, padding: "5%", display: "flex" }}>
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
          overflow: "hidden",
        }}
      >
        {/* Star scatter */}
        <svg
          aria-hidden
          viewBox="0 0 100 140"
          preserveAspectRatio="none"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.7 }}
        >
          {[[8,12,0.4],[22,6,0.5],[38,14,0.4],[56,8,0.5],[72,12,0.45],[88,6,0.5],[12,130,0.4],[32,134,0.5],[54,132,0.4],[76,128,0.5],[92,134,0.4]].map(([x,y,r],i) => (
            <circle key={i} cx={x} cy={y} r={r} fill={accent} />
          ))}
        </svg>

        <div style={{ fontFamily: "var(--font-ui)", fontSize: 7, fontWeight: 700, letterSpacing: "0.4em", color: accent, marginBottom: "5%", position: "relative" }}>
          TO&apos;Y MAROSIMI
        </div>

        {/* Celestial sun-moon medallion */}
        <div style={{ width: "60%", aspectRatio: "1/1", position: "relative", marginBottom: "5%" }}>
          <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: NIGHT_GRADIENT, padding: "5%", boxShadow: `0 0 0 1px ${accent}66` }}>
            <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: bg, padding: "4%", boxSizing: "border-box", border: `1px solid ${accent}44` }}>
              <div style={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", position: "relative" }}>
                <Photo
                  src={photo}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallback={
                    <svg viewBox="0 0 100 100" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
                      <defs>
                        <radialGradient id="w19-bg" cx="50%" cy="50%" r="55%">
                          <stop offset="0%" stopColor={accent} stopOpacity="0.32" />
                          <stop offset="100%" stopColor={bg} stopOpacity="0" />
                        </radialGradient>
                      </defs>
                      <rect width="100" height="100" fill="url(#w19-bg)" />
                      <g stroke={accent} strokeWidth="0.6" opacity="0.85">
                        {Array.from({ length: 16 }, (_, i) => {
                          const ang = (i / 16) * Math.PI * 2;
                          const outer = i % 2 === 0 ? 30 : 24;
                          return (
                            <line
                              key={i}
                              x1={50 + Math.cos(ang) * 18}
                              y1={50 + Math.sin(ang) * 18}
                              x2={50 + Math.cos(ang) * outer}
                              y2={50 + Math.sin(ang) * outer}
                            />
                          );
                        })}
                      </g>
                      <circle cx="50" cy="50" r="14" fill={accent} opacity="0.35" />
                      <circle cx="55" cy="46" r="12" fill={bg} />
                      <g fill={accent}>
                        <circle cx="22" cy="22" r="0.9" />
                        <circle cx="78" cy="24" r="1.1" />
                        <circle cx="18" cy="76" r="0.9" />
                        <circle cx="82" cy="78" r="1" />
                      </g>
                    </svg>
                  }
                />
                <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 50% 50%, transparent 60%, ${accent}22 100%)` }} />
              </div>
            </div>
          </div>
        </div>

        <div style={{ fontFamily: font, fontStyle: "italic", fontSize: 28, lineHeight: 1.05, color: ink, position: "relative" }}>
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div style={{ fontFamily: "var(--font-ui)", fontSize: 6.5, fontWeight: 700, letterSpacing: "0.32em", color: accent, marginTop: "4%", position: "relative" }}>
          YULDUZLAR OSTIDA · {captionDate}
        </div>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 6, fontWeight: 600, letterSpacing: "0.28em", color: `${ink}99`, marginTop: 6, position: "relative" }}>
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function formatDate(d: string): string {
  const m = d.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return d.toUpperCase();
  const months = [
    "YANVAR", "FEVRAL", "MART", "APREL", "MAY", "IYUN",
    "IYUL", "AVGUST", "SENTABR", "OKTABR", "NOYABR", "DEKABR",
  ];
  return `${parseInt(m[1], 10)} ${months[parseInt(m[2], 10) - 1] ?? ""} ${m[3]}`;
}
